import { Address, IInsurer } from './insurer.model';
import { IQueryParams } from './queryparams';
import { IBaseModel } from './base.model';
import { IRepresentative } from './representative.model';
import {
  IAssignedPaymentMethods,
  IInsurerCode,
  IIntermediary,
  IIntermediaryPaymentMethods,
} from './intermediary.model';
import { PaymentMethod } from './payment.model';
import { FormControl } from '@angular/forms';

export enum PolicyStatus {
  Active = 'Active',
  Draft = 'Draft',
  Expired = 'Expired',
  Cancelled = 'Cancelled',
  InClaim = 'InClaim',
}

export interface IBrokerInsurerRequest {
  insurerId: number;
  brokerId: number;
}

export interface IBrokerRepresentative extends IRepresentative {
  broker: IIntermediary;
  brokerId: number;
  insures: IInsurer[];
  insurerId: number;
}

export type BrokerType = 'Single' | 'Multi';

export interface IAgentUpdateRequest {
  name: string;
  logoUrl: string;
  taxPayerId: string;
  email: string;
  address: Partial<Address>;
  bankDetail: Partial<IBankDetail>;
  insurerId: number;
  insurerCodes?: IInsurerCode[] | null;
}

export interface IAgent extends IIntermediary {
  insurer: IInsurer;
}

export interface IBroker extends IIntermediary {
  insurer: IInsurer[];
  paymentMethod: IAssignedPaymentMethods[];
  revenueRates: IBrokerRevenueRate[];
  /** Comprehensive motor it issues needs an approved inspection first (the broker's own rule). */
  requireComprehensiveApproval?: boolean;
}

export interface IBrokerConfiguration {
  insurerId: number;
  brokerId: number;
}

export interface IBrokerUpdateRequest {
  name: string;
  logoUrl: string;
  taxPayerId: string;
  email: string;
  smsOriginatorId?: string;
  address: Partial<Address>;
  bankDetail: Partial<IBankDetail>;
  intermediaryPaymentType: PaymentMethod[];
  insurerId: number | null;
  /**
   * Revenue rate rows to upsert. Rows are keyed by (distributionChannel, insurerId); existing rows
   * matching a submitted key are replaced, those not in the list are left untouched. Removal needs
   * the explicit DELETE endpoint.
   */
  revenueRates?: IBrokerRevenueRate[];
  /**
   * Per-insurer payment method assignments.
   * When provided, takes precedence over the legacy paymentMethods + insurerId combination.
   */
  insurerPaymentMethods?: IBrokerInsurerPaymentMethodsRequest[];
  insurerCodes?: IInsurerCode[] | null;
}

export interface IBrokerInsurerPaymentMethodsRequest {
  insurerId: number;
  paymentMethods: PaymentMethod[];
}

export interface IBankDetail {
  name: string;
  branch: string;
  accountNumber: string;
  swiftCode?: string;
  accountName?: string;
}

export interface IBrokerQueryParams extends IQueryParams {
  InsurerId?: number | null;
  Query?: string | null;
}

export interface IbrokerStats {
  totalBrokers: number;
  activeBrokers: number;
  inactiveBrokers: number;
  totalInactiveBrokers?: number;
  totalSustainedBrokers?: number;
}

export type WalletTransactionType = 'Deposit' | 'Withdraw';

export enum IntermediaryType {
  Agent = 'Agent',
  Broker = 'Broker',
}

export type BrokerDocumentType =
  | 'Certificate'
  | 'License'
  | 'DirectorId'
  | 'DirectorProofOfResidence'
  | 'TaxClearanceCertificate'
  | 'CertificateOfIncorporation'
  | 'LatestAuditedFinancials'
  | 'ProofOfResidence'
  | 'POA'
  | 'PACRAForm';

export enum IntermediaryStatusType {
  Active = 'Active',
  Inactive = 'Inactive',
}

export type DebitNoteStatus = 'Paid' | 'NotPaid';

export interface IDebtorDebitNote extends IBaseModel {
  debitNoteNumber: string;
  status: DebitNoteStatus;
  amount: number;
}

export interface IDebtorAgeAnalysis {
  currency: string;
  debitNote: IDebtorDebitNote;
  allocatedAmount: number;
  outstandingAmount: number;
  currentPeriodOfDebt: number;
}

export interface IbrokerDocuments extends IBaseModel {
  description: string;
  url: string;
  type: string;
}

export interface IBrokerDocumentUploadRequest {
  description: string;
  files: File[];
  type: BrokerDocumentType;
}

export interface IBrokerDocumentUpload extends IBaseModel {
  description: string;
  files: File[];
  type: BrokerDocumentType;
}

export interface IBrokerPaymentMethod extends IAssignedPaymentMethods {
  brokerId: number;
}

export enum BrokerRevenueType {
  Gross = 'Gross',
  Commission = 'Commission',
}

export type BrokerRevenueChannel = 'Cash' | 'MobileMoney';
export type RateValueType = 'Percentage' | 'FixedAmount';

/** Where the sale came from, from the product's perspective — mirrors backend ProductDistributionChannels. */
export type ProductDistributionChannel =
  | 'Intermediary'
  | 'IntermediaryMobile'
  | 'SelfService'
  | 'SelfServiceWeb'
  | 'SelfServiceMobile'
  | 'SelfServiceUssd'
  | 'Direct';

/** Whether the insurer's product RevenueRate line still charges on top of this broker row. */
export type InsurerRateApplication = 'Skip' | 'Apply';

export interface IBrokerRevenueRateBand {
  channel: BrokerRevenueChannel;
  fromAmount: number;
  toAmount: number | null;
  valueType: RateValueType;
  value: number;
}

export interface IBrokerRevenueRate {
  type: BrokerRevenueType;
  rate: number;
  distributionChannel: ProductDistributionChannel;
  insurerRateApplication: InsurerRateApplication;
  /** Null = default row for this (broker, channel). Set = per-insurer override. */
  insurerId?: number | null;
  /** Optional amount bands per channel — when a band matches, it overrides rate/type for that transaction. */
  bands?: IBrokerRevenueRateBand[];
}

export interface IBrokerRevenueRateForm {
  type: FormControl<BrokerRevenueType>;
  rate: FormControl<number>;
  distributionChannel: FormControl<ProductDistributionChannel>;
  insurerRateApplication: FormControl<InsurerRateApplication>;
  insurerId: FormControl<number | null>;
}

export interface IBrokerMergeCandidate {
  brokerId: number;
  name: string;
  email: string;
  totalGwp: number;
  policyCount: number;
  representativeCount: number;
  clientCount: number;
}

export interface IBrokerDuplicateGroup {
  matchKey: string;
  matchType: 'TaxPayerId' | 'Email';
  brokers: IBrokerMergeCandidate[];
}

export interface IBrokerMergePreview {
  recommendedSurvivorId: number;
  candidates: IBrokerMergeCandidate[];
  reassignmentCounts: Record<string, number>;
}

export interface IBrokerMergeResult {
  mergeRecordId: number;
  survivorBrokerId: number;
  mergedBrokerIds: number[];
  reassignmentCounts: Record<string, number>;
}

export interface IBrokerMergeHistoryDetails {
  mergedBrokerIds: number[];
  reassignmentCounts: Record<string, number>;
  notes: string | null;
  rolledBackAt?: string | null;
  rolledBackByUserId?: number | null;
}

export interface IBrokerMergeHistoryRecord {
  id: number;
  survivorBrokerId: number;
  survivorBrokerName: string;
  performedByUserId: number;
  performedByUserName: string;
  createdAt: string;
  updatedAt: string;
  details: IBrokerMergeHistoryDetails;
  isRolledBack: boolean;
}

export interface IBrokerMergeRollbackResult {
  mergeRecordId: number;
  survivorBrokerId: number;
  restoredBrokerIds: number[];
  reassignmentsReverted: number;
  deletedRowsRestored: number;
}
